import { useState, useCallback } from 'react';
import { useField } from 'formik';

import { generateClassName } from '@klv/shared/utils/functions';

import PasswordStrength from '../../password-strength';
import {
  FieldGroup, Label, Input, Error, Description,
} from '..';

import * as styles from './styles/passwordInput.module.scss';

import type { IPasswordInput } from '../types';

const PasswordInput = ({
  showPasswordStrength = false,
  fieldGroupClassName,
  labelClassName,
  backgroundColor,
  id,
  name,
  label,
  description,
  maxLength,
  placeholder,
  autocomplete,
  inputClassName,
  autofocus,
  errorClassName,
  submitted = true,
  onFocus,
  ...rest
}: IPasswordInput) => {
  const [field, meta] = useField({ name, ...rest });
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    field.onBlur(e);
  }, [field.onBlur]);

  // Hide errors when password strength is shown and field is focused
  // Shows strength feedback instead of validation errors during typing
  const displayError = showPasswordStrength && isFocused ? undefined : meta.error;

  return (
    <FieldGroup className={fieldGroupClassName}>
      <Label className={labelClassName} $backgroundColor={backgroundColor} htmlFor={id || name}>{label}</Label>
      {description && description.length > 0 && (
        <Description $backgroundColor={backgroundColor}>{description}</Description>
      )}
      <Input 
        {...(maxLength ? { maxLength } : {})} 
        $backgroundColor={backgroundColor} 
        hasError={Boolean(meta.touched && displayError)} 
        placeholder={placeholder} 
        autoComplete={autocomplete} 
        type="password"
        className={generateClassName([
          showPasswordStrength && isFocused && styles.inputWithStrength,
          inputClassName,
        ])}
        {...field} 
        {...rest} 
        autoFocus={autofocus} 
        onFocus={handleFocus} 
        onBlur={handleBlur} 
      />
      {showPasswordStrength && (
        <PasswordStrength 
          value={field.value || ''} 
          isFocused={isFocused} 
        />
      )}
      {submitted && meta.touched && displayError && <Error className={errorClassName}>{displayError}</Error>}
    </FieldGroup>
  );
};

export default PasswordInput;
